import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import FormFieldContainer from '@components/ui/FormFieldContainer';
import InputField, { InputType } from '@components/ui/InputField';
import { usePdp } from '@context/PdpContext';
import { useGiftCard } from '@context/GiftCardContext';
import { validate } from 'email-validator';

import styles from './GiftCardForm.module.scss';

const GiftCardForm = () => {
  const { t } = useTranslation('pdp');
  const { addToCartClicked } = usePdp();
  const {
    giftCardRecipientsName,
    giftCardSendersName,
    giftCardRecipientsEmail,
    giftCardDeliveryDate,
    giftCardMessage,
    isValidEmail,
    minDeliveryDate,
    maxDeliveryDate,
    setGiftCardRecipientsName,
    setGiftCardSendersName,
    setGiftCardRecipientsEmail,
    setGiftCardDeliveryDate,
    setGiftCardMessage,
    setIsValidEmail,
  } = useGiftCard();

  useEffect(() => {
    const validatedEmail = validate(giftCardRecipientsEmail);
    setIsValidEmail(validatedEmail);
  }, [giftCardRecipientsEmail, setIsValidEmail]);

  const deliveryDateIsLessThanMinDeliveryDate =
    minDeliveryDate && giftCardDeliveryDate < minDeliveryDate;
  const deliveryDateIsMoreThanMaxDeliveryDate =
    maxDeliveryDate && giftCardDeliveryDate > maxDeliveryDate;

  return (
    <div>
      <FormFieldContainer
        label={t('product.giftcard.recipients.name')}
        htmlFor="giftcard-recipients-name"
      >
        <InputField
          id="giftcard-recipients-name"
          type={InputType.text}
          value={giftCardRecipientsName}
          aria-required="false"
          onChange={(value) => setGiftCardRecipientsName(value)}
        />
      </FormFieldContainer>

      <FormFieldContainer
        label={t('product.giftcard.senders.name')}
        htmlFor="giftcard-senders-name"
      >
        <InputField
          id="giftcard-senders-name"
          type={InputType.text}
          value={giftCardSendersName}
          aria-required="false"
          onChange={(value) => setGiftCardSendersName(value)}
        />
      </FormFieldContainer>

      <FormFieldContainer
        label={t('product.giftcard.recipients.email')}
        htmlFor="giftcard-recipients-email"
        errorMessage={
          (addToCartClicked &&
            !giftCardRecipientsEmail &&
            t('product.giftcard.required.value')) ||
          (addToCartClicked &&
            giftCardRecipientsEmail &&
            !isValidEmail &&
            t('product.giftcard.invalid.email'))
        }
      >
        <InputField
          id="giftcard-recipients-email"
          type={InputType.email}
          value={giftCardRecipientsEmail}
          onChange={(value) => setGiftCardRecipientsEmail(value)}
          hasError={
            (addToCartClicked && !giftCardRecipientsEmail) ||
            (addToCartClicked && !isValidEmail)
          }
        />
      </FormFieldContainer>

      <FormFieldContainer
        label={t('product.giftcard.delivery.date')}
        htmlFor="giftcard-delivery-date"
        errorMessage={
          (addToCartClicked && deliveryDateIsLessThanMinDeliveryDate) ||
          (addToCartClicked && deliveryDateIsMoreThanMaxDeliveryDate)
            ? t('product.giftcard.error.delivery.date', {
                minDate: minDeliveryDate,
                maxDate: maxDeliveryDate,
              })
            : addToCartClicked &&
              !giftCardDeliveryDate &&
              t('product.giftcard.required.value')
        }
      >
        <InputField
          id="giftcard-delivery-date"
          type={InputType.date}
          value={giftCardDeliveryDate}
          onChange={(value) => setGiftCardDeliveryDate(value)}
          hasError={
            (addToCartClicked && !giftCardDeliveryDate) ||
            (addToCartClicked && deliveryDateIsLessThanMinDeliveryDate) ||
            (addToCartClicked && deliveryDateIsMoreThanMaxDeliveryDate)
          }
          minDate={minDeliveryDate}
          maxDate={maxDeliveryDate}
        />
      </FormFieldContainer>

      <FormFieldContainer
        label={t('product.giftcard.message')}
        htmlFor="giftcard-message"
      >
        <textarea
          id="giftcard-message"
          className={styles.messageBox}
          value={giftCardMessage}
          onChange={(e) => setGiftCardMessage(e.target.value)}
          aria-required="false"
          rows={6}
          maxLength={200}
        />
        <span className={styles.messageHelperText}>
          {t('product.giftcard.message.helper')}
        </span>
      </FormFieldContainer>
    </div>
  );
};

export default GiftCardForm;
